import type { FileResponse, LaunchChecklistPatch, LaunchChecklistView } from '@doneverse/shared'
import { api, getActiveWorkspaceId, isNotFoundError } from '../api/index.js'
import { fileService } from './fileService.js'

/** Where a topic's attachments live, so they don't litter the root of /files. */
export const LAUNCH_CHECKLIST_FOLDER_NAME = 'Launch checklist'

/**
 * The folder id, remembered per workspace.
 *
 * Resolving it costs a full listing of the workspace root, and the API signs a
 * download URL for every upload in that listing — so re-resolving on each
 * attach makes a founder with a busy root pay for it ten times over. Keyed by
 * workspace because the id belongs to one: after a switch this is a miss, not a
 * stale id the API would reject with "parent_id must reference a folder in the
 * same workspace".
 */
let folderCache: { workspaceId: string | null; id: string | null } | null = null

/** Test seam: the cache is module state, and suites must not leak into each other. */
export function resetLaunchChecklistFolderCache(): void {
  folderCache = null
}

async function resolveFolderId(workspaceId: string | null): Promise<string | null> {
  if (folderCache?.workspaceId === workspaceId) return folderCache.id

  // A folder we cannot resolve is not a reason to lose the file — fall back to
  // the root, which is where it would have gone anyway. Not cached, so the next
  // attach tries again.
  const id = await fileService
    .findOrCreateFolder(LAUNCH_CHECKLIST_FOLDER_NAME)
    .then((folder) => folder.id)
    .catch(() => null)
  if (id !== null) folderCache = { workspaceId, id }
  return id
}

/**
 * Onboarding step 10's launch checklist — ClickUp 86d3ukp27.
 *
 * Both calls answer with the WHOLE view rather than the item that changed, so
 * the client never recomputes `checkedCount`, `allChecked` or `complete` from a
 * partial response — and never disagrees with the server about whether the step
 * just closed.
 */
export const launchChecklistService = {
  /**
   * The founder's checklist. A doer reading their assigned founder's copy uses
   * the same call: the workspace comes from the `x-doneverse-workspace-id`
   * header the api client injects, which is what a doer with several founders
   * needs (the API 400s without it).
   *
   * Null on a 404 — the founder has no onboarding run at all (the grandfathered
   * cohort, or a doer whose founder predates the tracker). That is an empty
   * state, not a failure, so it must not reach an error boundary.
   */
  fetch: async (): Promise<LaunchChecklistView | null> => {
    try {
      return await api.get('/v1/onboarding/launch-checklist').json<LaunchChecklistView>()
    } catch (error: unknown) {
      if (isNotFoundError(error)) return null
      throw error
    }
  },

  /**
   * Update one topic. Founder-only server-side.
   *
   * Send ONLY what changed: an omitted field is left alone and an explicit
   * `null` clears it, so echoing the whole item back would overwrite a note the
   * founder edited in another tab with the copy this tab happened to hold.
   */
  update: async (topicId: string, patch: LaunchChecklistPatch): Promise<LaunchChecklistView> =>
    api
      .patch(patch, `/v1/onboarding/launch-checklist/${encodeURIComponent(topicId)}`)
      .json<LaunchChecklistView>(),

  /**
   * Upload a topic's attachment and return the row whose `id` becomes `file_id`.
   *
   * Goes into the checklist's own folder rather than the workspace root, so a
   * document attached to "Access and tools" doesn't turn up in the founder's
   * Files browser next to their doer's deliverables.
   */
  uploadFile: async (file: File): Promise<FileResponse> => {
    const folderId = await resolveFolderId(getActiveWorkspaceId())
    return fileService.createUploadFile(file, file.name, folderId ?? undefined)
  },
}
